import React, { useState, useEffect } from 'react';
import { X, Users, FileText, Search, Loader } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import toast from 'react-hot-toast';

export default function AssignGroupModal({ isOpen, onClose, group, onSuccess }) {
  const { getAuthenticatedApi } = useAuth();
  const api = getAuthenticatedApi();
  
  const [members, setMembers] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [memberSearchQuery, setMemberSearchQuery] = useState('');
  const [assigningProgress, setAssigningProgress] = useState(null);

  useEffect(() => {
    if (isOpen && group) {
      setSelectedMembers([]);
      setMemberSearchQuery('');
      setAssigningProgress(null);
      fetchMembers();
    }
  }, [isOpen, group]);

  const fetchMembers = async () => {
    try {
      setLoadingMembers(true);
      const response = await api.get('/gyms.php?action=members');
      
      let activeMembers = [];
      if (Array.isArray(response.data)) {
        activeMembers = response.data.filter(m => 
          (m.active === 1 || m.active === '1') && 
          (m.role_name === 'user' || m.role_in_gym === 'member')
        );
      }
      setMembers(activeMembers);
    } catch (error) {
      console.error('Errore nel caricamento membri:', error);
      setMembers([]);
    } finally {
      setLoadingMembers(false);
    }
  };

  const toggleMember = (memberId) => {
    setSelectedMembers(prev => 
      prev.includes(memberId)
        ? prev.filter(id => id !== memberId)
        : [...prev, memberId]
    );
  };

  // Filtra membri in base alla ricerca
  const filteredMembers = members.filter(member => {
    if (!memberSearchQuery.trim()) return true;
    
    const query = memberSearchQuery.toLowerCase();
    const name = (member.name || '').toLowerCase();
    const username = (member.username || '').toLowerCase();
    const email = (member.email || '').toLowerCase();
    
    return name.includes(query) || username.includes(query) || email.includes(query);
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (selectedMembers.length === 0) {
      toast.error('Seleziona almeno un membro');
      return;
    }

    if (!group.templates || group.templates.length === 0) {
      toast.error('Il gruppo non contiene template da assegnare');
      return;
    }

    try {
      setLoading(true);
      setAssigningProgress({
        total: group.templates.length * selectedMembers.length,
        completed: 0,
        currentTemplate: null,
        currentMember: null
      });

      const templates = group.templates;
      const results = {
        success: 0,
        failed: 0,
        errors: []
      };

      // Assegna ogni template a ogni membro selezionato
      for (const template of templates) {
        setAssigningProgress(prev => ({
          ...prev,
          currentTemplate: template.name
        }));

        for (const memberId of selectedMembers) {
          setAssigningProgress(prev => ({
            ...prev,
            currentMember: members.find(m => m.id === memberId)?.name || '...',
            completed: prev.completed + 1
          }));

          try {
            const data = {
              template_id: template.id,
              member_ids: [memberId],
              scheda_name: template.name,
              scheda_description: template.description || null,
              use_max_weight: false
            };

            const response = await api.post('/gym_workout_templates.php?action=create_workout', data);
            
            if (response.data.success) {
              results.success++;
            } else {
              results.failed++;
              results.errors.push({
                template: template.name,
                member: members.find(m => m.id === memberId)?.name || 'Unknown',
                error: response.data.error || 'Errore sconosciuto'
              });
            }
          } catch (error) {
            results.failed++;
            results.errors.push({
              template: template.name,
              member: members.find(m => m.id === memberId)?.name || 'Unknown',
              error: error.response?.data?.error || 'Errore nella richiesta'
            });
          }

          // Piccola pausa per non sovraccaricare il server
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }

      setAssigningProgress(null);

      if (results.failed === 0) {
        toast.success(`Tutti i ${results.success} template assegnati con successo!`);
      } else if (results.success > 0) {
        toast.success(`${results.success} template assegnati, ${results.failed} errori`);
        console.error('Errori durante assegnazione:', results.errors);
      } else {
        toast.error('Errore nell\'assegnazione dei template');
        console.error('Errori durante assegnazione:', results.errors);
      }

      onSuccess();
      onClose();
    } catch (error) {
      console.error('Errore nell\'assegnazione del gruppo:', error);
      toast.error(error.response?.data?.error || 'Errore nell\'assegnazione del gruppo');
      setAssigningProgress(null);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const templatesCount = group?.templates?.length || 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-900 rounded-lg w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 dark:border-white/10 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Users className="h-5 w-5" />
              Assegna Gruppo
            </h2>
            <p className="text-sm text-gray-600 dark:text-white/65 mt-1">
              {group?.name} ({templatesCount} template)
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
            disabled={loading}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">
            {/* Info Gruppo */}
            <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-lg p-4">
              <h3 className="text-sm font-medium text-indigo-900 dark:text-indigo-300 mb-2">
                Template che verranno assegnati:
              </h3>
              <ul className="text-sm text-indigo-700 dark:text-indigo-400 space-y-1">
                {group?.templates?.map(template => (
                  <li key={template.id} className="flex items-center gap-2">
                    <FileText className="h-3 w-3" />
                    {template.name}
                  </li>
                )) || <li>Nessun template nel gruppo</li>}
              </ul>
            </div>

            {/* Progress Bar durante assegnazione */}
            {assigningProgress && (
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <div className="flex items-center gap-3 mb-2">
                  <Loader className="h-5 w-5 text-blue-600 dark:text-blue-400 animate-spin" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-blue-900 dark:text-blue-300">
                      Assegnazione in corso...
                    </p>
                    <p className="text-xs text-blue-700 dark:text-blue-400">
                      {assigningProgress.currentTemplate && assigningProgress.currentMember && (
                        <>
                          {assigningProgress.currentTemplate} → {assigningProgress.currentMember}
                        </>
                      )}
                    </p>
                  </div>
                </div>
                <div className="w-full bg-blue-200 dark:bg-blue-800 rounded-full h-2">
                  <div 
                    className="bg-blue-600 dark:bg-blue-400 h-2 rounded-full transition-all duration-300"
                    style={{ 
                      width: `${(assigningProgress.completed / assigningProgress.total) * 100}%` 
                    }}
                  />
                </div>
                <p className="text-xs text-blue-600 dark:text-blue-400 mt-2 text-right">
                  {assigningProgress.completed} / {assigningProgress.total}
                </p>
              </div>
            )}

            {/* Selezione Membri */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-gray-700 dark:text-white/85">
                  <Users className="h-4 w-4 inline mr-1" />
                  Assegna a Membri * ({selectedMembers.length} selezionati
                  {memberSearchQuery && filteredMembers.length !== members.length && 
                    ` - ${filteredMembers.length} di ${members.length} visibili`})
                </label>
              </div>

              {/* Filtro ricerca membri */}
              {members.length > 0 && (
                <div className="mb-3 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    value={memberSearchQuery}
                    onChange={(e) => setMemberSearchQuery(e.target.value)}
                    placeholder="Cerca membro per nome, username o email..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg 
                             bg-white dark:bg-gray-800 text-gray-900 dark:text-white
                             focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    disabled={loading}
                  />
                </div>
              )}

              {loadingMembers ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                  <p className="text-sm text-gray-600 dark:text-white/65 mt-2">Caricamento membri...</p>
                </div>
              ) : filteredMembers.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <Users className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-600 dark:text-white/65">
                    {memberSearchQuery 
                      ? `Nessun membro trovato per "${memberSearchQuery}"`
                      : 'Nessun membro trovato'}
                  </p>
                  {memberSearchQuery && members.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setMemberSearchQuery('')}
                      className="mt-2 text-xs text-indigo-600 dark:text-indigo-400 hover:underline"
                    >
                      Pulisci filtro
                    </button>
                  )}
                </div>
              ) : (
                <div className="border border-gray-300 dark:border-gray-700 rounded-lg max-h-64 overflow-y-auto">
                  {filteredMembers.map((member) => (
                    <label
                      key={member.id}
                      className="flex cursor-pointer items-center border-b border-gray-200 p-3 last:border-b-0
                               hover:bg-gray-50 dark:border-white/10 dark:hover:bg-white/5"
                    >
                      <input
                        type="checkbox"
                        checked={selectedMembers.includes(member.id)}
                        onChange={() => toggleMember(member.id)}
                        className="h-4 w-4 text-indigo-600 rounded"
                        disabled={loading}
                      />
                      <div className="ml-3 flex-1">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {member.name || member.username}
                        </p>
                        {member.email && (
                          <p className="text-xs text-gray-500 dark:text-white/65">
                            {member.email}
                          </p>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-gray-200 dark:border-white/10 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg px-4 py-2 text-gray-700 hover:bg-gray-100 dark:text-white/85 dark:hover:bg-white/10"
              disabled={loading}
            >
              Annulla
            </button>
            <button
              type="submit"
              disabled={loading || selectedMembers.length === 0 || templatesCount === 0}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700
                       disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loading ? (
                <>
                  <Loader className="h-4 w-4 animate-spin" />
                  Assegnazione...
                </>
              ) : (
                <>
                  <Users className="h-4 w-4" />
                  Assegna {templatesCount} Template
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

